import * as React from 'react';
import { FormattedMessage } from 'react-intl';

import { IconRegularCoupon } from '@notino/react-styleguide';
import { GetOrderCouponsQuery } from '@notino/shared/definitions/types';

import { IConditionalVoucherDiscount } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/ConditionalVoucherDiscount';
import { ConditionalInstructionsText } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/ConditionalVoucherDiscount/ConditionalInstructionsText';
import { messages } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/messages';
import { VoucherTerms } from '@containers/ProductDetailContainer/ProductDetail/ProductBaseInfo/VoucherDiscount/VoucherDiscountInfo/ProductDiscount/Terms';

import { useConditionVoucherPrice } from '../hooks/useConditionVoucherPrice';

import { DiscountProgressBar } from './DiscountProgressBar';
import { conditionVoucherInfoMessages } from './messages';
import {
  ConditionalDiscountProgressWrapper,
  ConditionalTextWrapper,
  DiscountDescription,
  DiscountInformationWrapper,
} from './styled';

interface ConditionalDiscountProgressProps {
  coupons: GetOrderCouponsQuery['GetOrderCoupons']['invalidCoupons']['coupons'];
  voucherDiscount: IConditionalVoucherDiscount;
}

export const ConditionalDiscountProgress: React.FC<
  ConditionalDiscountProgressProps
> = ({ coupons, voucherDiscount }) => {
  const {
    code,
    discountType,
    conditionType,
    discountConditions: [{ discountAmount, percentageDiscount }],
  } = voucherDiscount;

  const absoluteDiscountAmount = useConditionVoucherPrice(discountAmount);

  const filteredCouponByCode = coupons.find(
    (coupon) => coupon.code.toLowerCase() === code.toLowerCase()
  );

  if (!filteredCouponByCode) {
    return null;
  }

  if (filteredCouponByCode.validationErrors?.length === 0) {
    return null;
  }

  const { validationErrors } = filteredCouponByCode;

  if (
    !validationErrors[0]?.minLevels ||
    validationErrors[0]?.minLevels.length === 0
  ) {
    return null;
  }

  const { threshold, missing } = validationErrors[0].minLevels[0];

  const isPercentageType = discountType === 'Percentage';

  return (
    <ConditionalDiscountProgressWrapper>
      <DiscountInformationWrapper>
        <IconRegularCoupon
          color="icon.highlight"
          width="1.25rem"
          height="1.25rem"
        />

        <DiscountDescription>
          <ConditionalTextWrapper>
            <FormattedMessage
              {...conditionVoucherInfoMessages[
                isPercentageType
                  ? `discountAmountPercentageFrom${conditionType}`
                  : `discountAmountAbsoluteFrom${conditionType}`
              ]}
              values={{
                targetAmount: missing,
                coupon: filteredCouponByCode.code,
                discount: isPercentageType
                  ? percentageDiscount
                  : absoluteDiscountAmount,
                b: (chunks) => <b>{chunks}</b>,
              }}
            />
          </ConditionalTextWrapper>

          <VoucherTerms
            infoTooltipText={
              voucherDiscount.productsOnSaleIncluded ? (
                <FormattedMessage
                  {...messages.conditionalTooltipSaleIncludedText}
                />
              ) : (
                <FormattedMessage {...messages.conditionalTooltipText} />
              )
            }
            instructionsText={
              <ConditionalInstructionsText discount={voucherDiscount} />
            }
          />
        </DiscountDescription>
      </DiscountInformationWrapper>

      <DiscountProgressBar missing={missing} threshold={threshold} />
    </ConditionalDiscountProgressWrapper>
  );
};
